// src/components/Registration/steps/Step4Agreement.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

const PEACH = '#EBA477';
const API = process.env.REACT_APP_API_URL || '';

const FrostedInput = ({ style, ...props }) => (
  <input
    className="frosted-input"
    {...props}
    style={{
      width: '100%',
      height: 56,
      borderRadius: 14,
      border: '1px solid rgba(255,255,255,0.10)',
      background: 'rgba(255,255,255,0.12)',
      color: 'white',
      fontSize: 16,
      padding: '0 14px',
      outline: 'none',
      boxSizing: 'border-box',
      ...style,
    }}
  />
);

const FrostedSelect = ({ style, children, ...props }) => (
  <select
    className="frosted-input"
    {...props}
    style={{
      width: '100%',
      height: 56,
      borderRadius: 14,
      border: '1px solid rgba(255,255,255,0.10)',
      background: 'rgba(255,255,255,0.12)',
      color: 'white',
      fontSize: 16,
      padding: '0 12px',
      outline: 'none',
      boxSizing: 'border-box',
      appearance: 'none',
      ...style,
    }}
  >
    {children}
  </select>
);

export default function Step4Agreement({
  data = {},
  step1Data = {},
  onNext,
  onPrevious,
  loading,
  isFirstStep,
  onBindNext,
}) {
  const agreementVersion = '2026-01-15';
  const todayISO = useMemo(() => new Date().toISOString().slice(0, 10), []);

  // --- hydrate legacy/new keys (back-compat preserved) ---
  const initialAccepted =
    (typeof data.acceptedTerms === 'boolean' ? data.acceptedTerms : undefined) ??
    (typeof data.acceptedTC === 'boolean' ? data.acceptedTC : undefined) ??
    (typeof data.legal?.acceptedTC === 'boolean' ? data.legal.acceptedTC : undefined) ??
    false;

  const initialMarketing =
    (typeof data.marketingOptIn === 'boolean' ? data.marketingOptIn : undefined) ??
    (typeof data.legal?.marketingOptIn === 'boolean' ? data.legal.marketingOptIn : undefined) ??
    false;

  const sigObj = data.signature || {};
  const initialSigMode = sigObj.type || data.signatureType || 'draw';
  const initialTyped = sigObj.typedSignature || data.typedSignature || '';
  const initialDataUrl = sigObj.dataUrl || data.signatureDataUrl || '';

  const initialSignerName =
    sigObj.name ||
    data.signerName ||
    step1Data.fullName ||
    '';
  const initialSignerTitle =
    sigObj.title ||
    data.signerTitle ||
    step1Data.role ||
    'Owner';

  // signer
  const [signerName, setSignerName] = useState(initialSignerName);
  const [signerTitle, setSignerTitle] = useState(initialSignerTitle);

  // acceptance
  const [acceptedTC, setAcceptedTC] = useState(Boolean(initialAccepted));
  // keep marketingOptIn for payload/back-compat, but no UI control for it
  const [marketingOptIn] = useState(Boolean(initialMarketing));

  // signature
  const [sigMode, setSigMode] = useState(initialSigMode); // 'draw' | 'type'
  const [typedSig, setTypedSig] = useState(initialTyped);
  const [drawnSig, setDrawnSig] = useState(initialDataUrl);
  const canvasRef = useRef(null);
  const drawing = useRef(false);

  // terms content (backend -> silent fallback to inline)
  const [termsHtml, setTermsHtml] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!API) {
        if (!cancelled) setTermsHtml(defaultInlineTerms(agreementVersion));
        return;
      }
      try {
        const res = await fetch(
          `${String(API).replace(/\/+$/, '')}/api/legal/terms?format=html&version=${encodeURIComponent(agreementVersion)}`,
          { credentials: 'include' }
        );
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const html = await res.text();
        if (!cancelled) setTermsHtml(html || defaultInlineTerms(agreementVersion));
      } catch {
        if (!cancelled) setTermsHtml(defaultInlineTerms(agreementVersion));
      }
    })();
    return () => { cancelled = true; };
  }, [agreementVersion]);

  // Init / reset canvas
  useEffect(() => {
    if (sigMode !== 'draw') return;
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    const cssW = 520;
    const cssH = 180;
    const scale = window.devicePixelRatio || 1;
    canvas.width = Math.floor(cssW * scale);
    canvas.height = Math.floor(cssH * scale);
    canvas.style.width = `${cssW}px`;
    canvas.style.height = `${cssH}px`;
    ctx.setTransform(scale, 0, 0, scale, 0, 0);

    ctx.fillStyle = 'rgba(255,255,255,0.04)';
    ctx.fillRect(0, 0, cssW, cssH);
    ctx.strokeStyle = 'rgba(255,255,255,0.25)';
    ctx.strokeRect(0.5, 0.5, cssW - 1, cssH - 1);
  }, [sigMode]);

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const w = canvas.width / (window.devicePixelRatio || 1);
    const h = canvas.height / (window.devicePixelRatio || 1);
    ctx.clearRect(0, 0, w, h);
    ctx.fillStyle = 'rgba(255,255,255,0.04)';
    ctx.fillRect(0, 0, w, h);
    ctx.strokeStyle = 'rgba(255,255,255,0.25)';
    ctx.strokeRect(0.5, 0.5, w - 1, h - 1);
    setDrawnSig('');
  };

  const saveCanvasAsDataUrl = () => {
    const canvas = canvasRef.current;
    if (!canvas) return '';
    const url = canvas.toDataURL('image/png');
    setDrawnSig(url);
    return url;
  };

  // drawing events
  const onPointerDown = (e) => {
    if (sigMode !== 'draw') return;
    drawing.current = true;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    ctx.beginPath();
    ctx.moveTo(e.clientX - rect.left, e.clientY - rect.top);
  };
  const onPointerMove = (e) => {
    if (sigMode !== 'draw' || !drawing.current) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.lineWidth = 2.2;
    ctx.strokeStyle = '#ffffff';
    const rect = canvas.getBoundingClientRect();
    ctx.lineTo(e.clientX - rect.left, e.clientY - rect.top);
    ctx.stroke();
  };
  const onPointerUp = () => {
    if (sigMode !== 'draw') return;
    drawing.current = false;
    saveCanvasAsDataUrl();
  };

  const signatureReady = useMemo(
    () => (sigMode === 'draw' ? Boolean(drawnSig) : Boolean(typedSig.trim())),
    [sigMode, drawnSig, typedSig]
  );

  const canProceed = useMemo(
    () => acceptedTC && signerName.trim() && signatureReady && !loading,
    [acceptedTC, signerName, signatureReady, loading]
  );

  const buildAgreementPayload = useCallback((forceSignatureCapture = false) => {
    const signedAtISO = new Date().toISOString();
    const signature = {
      name: signerName.trim(),
      title: (signerTitle || '').trim(),
      signedAt: signedAtISO,
      type: sigMode,
      typedSignature: sigMode === 'type' ? typedSig.trim() : '',
      dataUrl:
        sigMode === 'draw'
          ? (drawnSig || (forceSignatureCapture ? saveCanvasAsDataUrl() : ''))
          : '',
    };

    const legal = {
      version: agreementVersion,
      acceptedTC: Boolean(acceptedTC),
      marketingOptIn: Boolean(marketingOptIn),
      acceptedAt: acceptedTC ? signedAtISO : '',
      signerName: signature.name,
      signerTitle: signature.title,
      signatureType: signature.type,
      typedSignature: signature.typedSignature,
      signatureDataUrl: signature.dataUrl,
      effectiveDate: todayISO,
      agreements: acceptedTC
        ? [
            {
              type: 'tos',
              version: agreementVersion,
              url: '',
              signedAt: signedAtISO,
              signerName: signature.name,
              signerEmail: (step1Data?.email || '').toLowerCase().trim(),
              __accepted: true,
            },
          ]
        : [],
      consents: [
        {
          type: 'marketing',
          accepted: Boolean(marketingOptIn),
          acceptedAt: acceptedTC ? signedAtISO : '',
          termsVersion: agreementVersion,
        },
      ],
    };

    return {
      acceptedTerms: Boolean(acceptedTC),
      marketingOptIn: Boolean(marketingOptIn),
      signature,
      legal,
    };
  }, [
    acceptedTC,
    agreementVersion,
    drawnSig,
    marketingOptIn,
    sigMode,
    signerName,
    signerTitle,
    step1Data?.email,
    todayISO,
    typedSig,
  ]);

  // Bind Next/› — send BOTH: legacy "legal" keys AND arrays for agreements/consents
  useEffect(() => {
    const handleSubmit = () => {
      if (!canProceed) return;

      onNext?.(buildAgreementPayload(true));
    };

    onBindNext?.({ onClick: handleSubmit, disabled: !canProceed });
    return () => onBindNext?.({ onClick: null, disabled: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    canProceed,
    buildAgreementPayload,
  ]);

  return (
    <div style={{ color: 'white' }}>
      <h2 style={{ marginTop: 0, marginBottom: 8 }}>Agreement &amp; Signature</h2>
      <p style={{ marginTop: 0, opacity: 0.9 }}>
        Please review the Terms &amp; Conditions and sign below.
      </p>

      {/* Scrollable Agreement */}
      <div
        role="region"
        aria-label="Terms & Conditions"
        tabIndex={0}
        style={{
          maxHeight: 360,
          overflowY: 'auto',
          borderRadius: 14,
          border: '1px solid rgba(255,255,255,0.18)',
          background: 'rgba(255,255,255,0.06)',
          padding: 16,
          lineHeight: 1.55,
          boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.06)',
        }}
        dangerouslySetInnerHTML={{ __html: termsHtml }}
      />

      {/* Checkboxes */}
      <div style={{ marginTop: 14, display: 'grid', gap: 10 }}>
        <label style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <input
            type="checkbox"
            checked={acceptedTC}
            onChange={(e) => setAcceptedTC(e.target.checked)}
            style={{ width: 18, height: 18, accentColor: PEACH }}
          />
          <span>I have read and accept the Terms &amp; Conditions.</span>
        </label>

        {/* Marketing checkbox removed per request */}
      </div>

      {/* Signer */}
      <h3 style={{ marginTop: 18, marginBottom: 8 }}>Signer</h3>
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 12 }}>
        <FrostedInput
          placeholder="Full name"
          value={signerName}
          onChange={(e) => setSignerName(e.target.value)}
        />
        <FrostedSelect
          value={signerTitle}
          onChange={(e) => setSignerTitle(e.target.value)}
        >
          {['Owner', 'Admin', 'Manager', 'Authorized Signer', 'Other'].map((r) => (
            <option key={r} value={r}>{r}</option>
          ))}
        </FrostedSelect>
      </div>

      {/* Signature */}
      <h3 style={{ marginTop: 18, marginBottom: 8 }}>Digital Signature</h3>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ display: 'flex', gap: 8 }}>
          <button type="button" onClick={() => setSigMode('draw')} style={sigMode === 'draw' ? tabActive : tab}>
            Draw
          </button>
          <button type="button" onClick={() => setSigMode('type')} style={sigMode === 'type' ? tabActive : tab}>
            Type
          </button>
        </div>
        <div style={{ opacity: 0.9 }}>Date: {todayISO}</div>
      </div>

      {sigMode === 'draw' ? (
        <div style={{ marginTop: 10 }}>
          <canvas
            ref={canvasRef}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerLeave={onPointerUp}
            style={{ borderRadius: 12, touchAction: 'none', cursor: 'crosshair' }}
          />
          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
            <button type="button" onClick={clearCanvas} style={smallBtn}>Clear</button>
            <button type="button" onClick={saveCanvasAsDataUrl} style={smallBtn}>Save</button>
          </div>
          {drawnSig ? (
            <div style={{ marginTop: 8, opacity: 0.9, fontSize: 13 }}>Signature captured ✓</div>
          ) : null}
        </div>
      ) : (
        <div style={{ marginTop: 10, maxWidth: 520 }}>
          <FrostedInput
            placeholder="Type your full name as your signature"
            value={typedSig}
            onChange={(e) => setTypedSig(e.target.value)}
          />
          <div style={{ marginTop: 8, opacity: 0.9, fontSize: 13 }}>
            Your typed name will be stored as your digital signature.
          </div>
        </div>
      )}

      {/* Back link */}
      {!isFirstStep && (
        <button
          type="button"
          onClick={() => onPrevious?.(buildAgreementPayload(false))}
          style={{
            marginTop: 18,
            background: 'transparent',
            border: 'none',
            color: 'rgba(255,255,255,0.9)',
            textDecoration: 'underline',
            cursor: 'pointer',
          }}
        >
          Back
        </button>
      )}

      <style>{`.frosted-input::placeholder{color:rgba(255,255,255,0.75);}`}</style>
    </div>
  );
}

/* Inline fallback (HTML string) */
function stylizeRypilHtml(html) {
  return String(html || '').replace(/\brypil\b/gi, '<span class="rypil-word">rypil</span>');
}

function defaultInlineTerms(version) {
  return stylizeRypilHtml(`
  <div style="font-weight:700;font-size:18px;margin-bottom:6px">rypil Business and Nonprofit Terms Conditions and Commercial Co-Venture Agreement</div>
  <div style="opacity:0.9;margin-bottom:10px">
    <div><strong>Version:</strong> 1</div>
    <div><strong>Updated:</strong> January 15, 2026</div>
    <div style="margin-top:8px">These Terms and Conditions (“Terms”) govern participation by businesses (“Business” or “Partner”) and nonprofits (“Nonprofits” or “NFPs”) in the rypil platform (“Platform”), operated by rypil Technologies, Inc. (“rypil,” “we,” “us,” or “our”). By registering as a rypil business or rypil nonprofit partner, or by continuing to participate in the rypil platform, you agree to these Terms.</div>
  </div>
  <hr style="border-color:rgba(255,255,255,0.18);margin:12px 0"/>

  <h4 style="margin:8px 0">1. Definitions</h4>
  <div style="margin-bottom:10px">
    <p><strong>“Contribution”</strong> means the amount funded by a business to a nonprofit through rypil as a result of consumer transactions.</p>
    <p><strong>“Donation”</strong> and <strong>“Contribution”</strong> are used interchangeably to describe payments made by a business to a nonprofit beneficiary.</p>
    <p><strong>“Platform”</strong> means the rypil technology ecosystem, including rypil.com, rypil4biz.com, mobile applications, and related services.</p>
    <p><strong>“Registered User”</strong> means a consumer who has created a rypil account and selected a nonprofit beneficiary within the platform.</p>
  </div>

  <h4 style="margin:8px 0">2. Participation and Eligibility</h4>
  <div style="margin-bottom:10px">
    <p><strong>2.1 Eligibility Requirements.</strong> Businesses must maintain an active account in good standing with rypil and process consumer transactions through a payment processor. Nonprofit must be a 501(c)(3) or 501(c)(6) public charity, or other eligible charitable designation, and be in good standing.</p>
    <p><strong>2.2 Eligibility Cap.</strong> To ensure the timely and accurate distribution of contributions, rypil implements the following controls:</p>
    <p><em>A. Suspension for Unresponsiveness.</em> rypil will automatically suspend a nonprofit account if the organization is unresponsive for a period of ninety (90) consecutive days (e.g., failure to provide necessary compliance or payment information).</p>
    <p><em>B. Distribution of Stalled Funds.</em> If remittance to a nonprofit partner cannot occur for 90 consecutive days or if the organization is no longer operating, rypil reserves the right to distribute any remaining balance among other registered nonprofit organizations on the Platform.</p>
  </div>

  <h4 style="margin:8px 0">3. Nature of Role and Relationship</h4>
  <div style="margin-bottom:10px">
    <p><strong>3.1 Independent Parties.</strong> Each Business and NFP acts as an independent entity. Nothing in these Terms constitutes a joint venture, partnership, franchise, or employment relationship.</p>
    <p><strong>3.2 Facilitator Role.</strong> rypil facilitates the connection and distribution of donations but is not a custodian of funds intended for partner nonprofits. rypil controls only the periods and processes of distribution, not the donation amount or timing, which are determined by the business’s consumer activity.</p>
    <p><strong>3.3 Pause or Termination.</strong> Businesses or NFPs may pause, remove, or deactivate participation at any time by submitting a written request through the “Contact Us” section of rypil.com or rypil4biz.com. Deactivation will occur within thirty (30) business days of receipt for Business and nonprofit partners.</p>
  </div>

  <h4 style="margin:8px 0">4. Business Funding and Fees</h4>
  <div style="margin-bottom:10px">
    <p><strong>4.1 Payment/Funding Mechanism.</strong> Businesses fund payment to the rypil platform through a rypil-designated payment processing solution. Nonprofits accept their donations/contributions through a rypil-designated payment processing solution.</p>
    <p><strong>4.2 Business Wallet Credits.</strong> rypil credits the wallet for the full amount funded, less (i) a rypil payment processing fee for services rendered and (ii) the exact third-party processing fees (at cost), which rypil reserves the right to pass directly through to the Business.</p>
    <p><strong>4.3 Transaction Structure.</strong> Each transaction by a registered rypil consumer user at a rypil registered business generates:</p>
    <ul>
      <li>A contribution/donation amount (minimum 2%), configurable per business.</li>
      <li>A platform fee of minimum 1.00% for businesses opted into auto payment or minimum 1.75% for all other payment options.</li>
      <li>A “transaction” means the total purchase amount. For businesses where tipping is routine and customary, the total purchase amount is tip-adjusted.</li>
    </ul>
    <p><strong>4.4 Refunds and Chargebacks.</strong></p>
    <ul>
      <li>Refund period: Reversed, adjusted, or voided transactions &lt; 30 days from the original transaction.</li>
      <li>Full refunds: Contributions are fully reversed only when the payment processor reverses the entire transaction.</li>
      <li>Partial refunds: Contribution recalculated pro-rata.</li>
      <li>Chargebacks: Always honored; contributions reversed based on payment processor outcomes.</li>
      <li>rypil fees: Not subject to any full or partial refund unless the transaction is voided for fraud.</li>
      <li>Refund processing: Adjustments queued for the next payout period for NFPs and within 30 days for businesses.</li>
    </ul>
  </div>

  <h4 style="margin:8px 0">5. Consumer Transaction Flow</h4>
  <div style="margin-bottom:10px">
    <p>When a registered rypil user shops at a rypil registered business, that business makes a contribution/donation to the nonprofit the consumer selected through the rypil platform. Donations/contributions are calculated as a percentage of total purchase amounts (tip-adjusted when applicable) and are facilitated by rypil.</p>
    <p>The donation/contribution is made by the rypil registered business, and neither rypil nor the rypil registered consumer/user is the donor.</p>
  </div>

  <h4 style="margin:8px 0">6. Data and Intellectual Property Rights</h4>
  <div style="margin-bottom:10px">
    <p><strong>6.1 Ownership.</strong> rypil retains ownership of all technology, data, reporting, and intellectual property generated by or through the platform. Businesses and NFPs retain ownership of their existing data and trademarks but grant rypil access to use necessary data solely to perform platform operations.</p>
    <p><strong>6.2 Data Usage and Privacy.</strong> rypil may aggregate, anonymize, and analyze platform data for analytics, reporting, and service improvement. In facilitating the contribution, rypil receives only: (a) business identifier and location, (b) transaction amount, and (c) last four (4) digits of the consumer's payment card. rypil does not receive, process, or store full payment card number, cardholder name, billing address, or other sensitive financial data. Personal or identifiable consumer data is never shared with third parties without explicit consent or as required by law.</p>
    <p><strong>6.3 Data Security.</strong> rypil uses industry-standard encryption and security protocols to protect all data within the platform, including partner administrative data and limited consumer transaction data, from unauthorized access. Payment facilitation partners are solely responsible for PCI DSS compliance of full card and sensitive financial data.</p>
    <p><strong>6.4 Termination of IP License.</strong> On termination or suspension, all trademark/IP licenses immediately terminate. Partner must remove rypil branding/materials within seven (7) days. This provision survives termination.</p>
  </div>

  <h4 style="margin:8px 0">7. Platform Communication and TCPA Compliance</h4>
  <div style="margin-bottom:10px">
    <p><strong>7.1 Administrative Texts.</strong> By registering, the Business/NFP consents to receive administrative text messages from rypil (e.g., security codes, account alerts, and funding updates).</p>
    <p><strong>7.2 Responsibility for Independent Outreach.</strong> If a Business/NFP uses data obtained through rypil to contact a Registered User via SMS outside of the rypil platform, the Business/NFP is solely responsible for obtaining the necessary legal consent.</p>
    <p><strong>7.3 Honoring the “Stop”.</strong> rypil maintains a platform-wide “Master Opt-Out” list. If a user opts out of rypil communications, the Business agrees not to use rypil-provided data to circumvent that choice.</p>
    <p><strong>7.4 Indemnification.</strong> The Business/NFP agrees to indemnify rypil for legal claims arising from unauthorized or non-compliant text communications.</p>
  </div>

  <h4 style="margin:8px 0">8. Dispute Resolution</h4>
  <div style="margin-bottom:10px">
    <p><strong>8.1 Arbitration.</strong> All disputes shall be resolved by binding arbitration administered by AAA under its Commercial Arbitration Rules in Cook County, Illinois, unless mutually agreed otherwise.</p>
    <p><strong>8.2 Class Action Waiver.</strong> All claims must be brought in an individual capacity; no class or collective actions are permitted.</p>
    <p><strong>8.3 Governing Law.</strong> These Terms are governed by Illinois law, without regard to conflict-of-law principles.</p>
  </div>

  <h4 style="margin:8px 0">9. Suspension and Termination</h4>
  <div style="margin-bottom:10px">
    <p>rypil may suspend or terminate any Business or NFP account that violates these Terms, provides inaccurate information, or engages in conduct detrimental to platform integrity. For material breaches (excluding fraud, abuse, or imminent harm), rypil will provide seven (7) days' written notice to cure before suspension or termination. Upon termination, unpaid or pending donations are processed per rypil’s distribution schedule. Section 6.4 obligations apply immediately.</p>
  </div>

  <h4 style="margin:8px 0">10. Limitation of Liability</h4>
  <div style="margin-bottom:10px">
    <p>rypil is not liable for indirect, incidental, or consequential damages arising from participation. Total direct liability shall not exceed total platform fees paid by the business in the six (6) months preceding the claim.</p>
  </div>

  <h4 style="margin:8px 0">11. Amendments</h4>
  <div style="margin-bottom:10px">
    <p>rypil may update these Terms from time to time. Non-material changes take effect upon posting. For material changes (e.g., fees, liability, termination), rypil will notify partner Businesses and Nonprofits via registered email at least 30 days prior to effective date.</p>
  </div>

  <h4 style="margin:8px 0">12. Reporting and Recordkeeping</h4>
  <div style="margin-bottom:10px">
    <p><strong>12.1 Periodic Reporting.</strong> rypil provides periodic reports summarizing contribution/distribution activity, fees, and adjustments.</p>
    <p><strong>12.2 Purpose of Reporting.</strong> Reports support transparency, reconciliation, and compliance with state commercial co-venture laws.</p>
    <p><strong>12.3 Obligation to Review.</strong> Businesses and Nonprofits must review each report and maintain copies for at least three (3) years.</p>
    <p><strong>12.4 Authority for Attestation.</strong> rypil may require electronic acknowledgment and attestation of report accuracy.</p>
  </div>

  <h4 style="margin:8px 0">13. Role-Specific Terms</h4>
  <div style="margin-bottom:10px">
    <p><strong>A. Businesses (Commercial Co-Venturers).</strong> Minimum donation commitment of 2% of each eligible transaction. Beneficiaries are selected by registered rypil users. Businesses must maintain records for three (3) years and consent to required regulatory filings.</p>
    <p><strong>B. Nonprofit.</strong> Must maintain eligible charitable status and use funds for charitable purposes. Nonprofit grants listing/co-marketing rights to rypil per brand guidelines, maintains records for three (3) years, and consents to required filings. Nonprofit is solely responsible for internal parent/chapter apportionments of received contributions.</p>
  </div>

  <h4 style="margin:8px 0">14. Filing Utility and Compliance</h4>
  <div style="margin-bottom:10px">
    <p>This Agreement is intended as a universal Commercial Co-Venture Agreement. rypil may file/register this Agreement and schedules of participating entities in jurisdictions requiring registration/reporting. Each business and nonprofit consents to such filings.</p>
  </div>

  <h4 style="margin:8px 0">Appendix A — Summary of Consumer Donation Flow</h4>
  <div style="margin-bottom:4px">
    <p>When you shop at a rypil registered business as a registered rypil user, that business makes a donation/contribution to the nonprofit you selected through the rypil platform. The donation is made by the business, and neither rypil nor the consumer is the donor.</p>
  </div>

  <div style="margin-top:10px;opacity:0.8;font-size:12px">Version: ${version}</div>
  `);
}

/* --- tiny styles --- */
const tab = {
  background: 'rgba(255,255,255,0.10)',
  border: '1px solid rgba(255,255,255,0.20)',
  color: 'white',
  borderRadius: 10,
  padding: '8px 12px',
  cursor: 'pointer',
};
const tabActive = { ...tab, borderColor: PEACH, background: 'rgba(235,164,119,0.25)' };
const smallBtn = {
  background: 'rgba(255,255,255,0.12)',
  border: '1px solid rgba(255,255,255,0.18)',
  color: 'white',
  borderRadius: 10,
  padding: '8px 12px',
  cursor: 'pointer',
};
